import type {
  Access,
  AccessBlockReason,
  AuthSessionState,
  AuthStatus,
  Branch,
  LocationSummary,
  MePayload,
  Organization,
  Plan,
  Subscription,
  User,
  UserWire,
} from './types';
import { normalizeAccess } from './normalizeAccess';

function enrichUser(wire: UserWire, organization: Organization, access: Access | null): User {
  return {
    id: wire.id,
    name: wire.name,
    last_name: wire.last_name,
    email: wire.email,
    phone: wire.phone,
    avatar_url: wire.avatar_url,
    ascend_id: wire.ascend_id,
    acsend_id: wire.ascend_id ?? '',
    role: {
      id: 0,
      name: access?.role ?? '',
      description: '',
    },
    organization,
    permission: (access?.permissions ?? []).map((name, index) => ({ id: index, name })),
  };
}

function mapPlans(plans: Plan[] | undefined): Subscription[] {
  return (plans ?? []).map((plan, index) => ({
    plan_id: index,
    status: plan.status,
    end_date: plan.end_date,
    next_billing_date: null,
    plan: {
      id: index,
      product_name: plan.product_name,
      name: plan.name,
      price: '',
      billing_cycle: plan.billing_cycle,
      is_demo: plan.billing_cycle === 'trial',
      demo_duration_days: null,
    },
  }));
}

function mapBranches(locations: LocationSummary[]): Branch[] {
  return locations.map((location) => ({
    id: location.id,
    name: location.name,
    ascend_id: location.ascend_id,
  }));
}

export function sessionPayloadToState(payload: MePayload): AuthSessionState {
  const access =
    payload.access === undefined
      ? null
      : payload.access === null
        ? null
        : normalizeAccess(payload.access);

  // Which of `user` / `admin` is present tells you the principal kind. An organization
  // account has an `admin` block and no `access` key at all.
  const isOrganizationAccount = !payload.user && !!payload.admin;
  const principal = payload.user ?? payload.admin ?? null;

  return {
    organization: payload.organization,
    locations: payload.locations,
    user: principal ? enrichUser(principal, payload.organization, access) : null,
    access,
    principalKind: principal ? (isOrganizationAccount ? 'organization' : 'user') : null,
    branches: mapBranches(payload.locations),
    subscriptions: mapPlans(access?.plans),
    credits: access?.credits ?? [],
  };
}

export function computeExpiresAt(expiresIn: number): number {
  return Date.now() + expiresIn * 1000;
}

export function holdsAppPermission(access: Access | null, clientId: string): boolean {
  if (!access) return false;
  return (access.permissions as string[]).includes(clientId);
}

/**
 * `access` is `null` when unscoped and absent entirely for an organization account —
 * two different states, and neither should bounce the user back to the login screen.
 */
export function resolveSessionStatus(
  state: AuthSessionState,
  clientId: string,
): { status: AuthStatus; blockReason: AccessBlockReason | null } {
  if (!state.user) {
    return { status: 'unauthenticated', blockReason: null };
  }

  if (state.principalKind === 'organization') {
    return { status: 'forbidden', blockReason: 'organization_account' };
  }

  if (!state.access) {
    return { status: 'forbidden', blockReason: 'location_required' };
  }

  if (!holdsAppPermission(state.access, clientId)) {
    return { status: 'forbidden', blockReason: 'app_not_permitted' };
  }

  return { status: 'authenticated', blockReason: null };
}

export function hasActivePlan(access: Access | null): boolean {
  return (access?.plans ?? []).some((plan) => plan.status === 'active');
}

export function creditBalance(access: Access | null, type: string): number {
  return access?.credits.find((credit) => credit.type === type)?.balance ?? 0;
}
